/**
 * Addendum lifecycle for the report editor: modal visibility, the
 * normalized "previous addendums" list, the create-addendum mutation flow,
 * and the open-addendum-by-other-user derivation that the finalize/save
 * pipelines need.
 *
 * The handler chooses between opening the modal (when settings say so) and
 * auto-submitting a CONCURRENCE addendum. Server's `invalidatesTags` on
 * the mutation re-triggers getStudyReports; we still update the local
 * `previousAddendums` list optimistically for snappy UI.
 */

import { useCallback, useEffect, useState } from 'react';
import type { notification } from 'antd';
import { Report as MReport } from '@/models/report';

type NotificationApi = ReturnType<typeof notification.useNotification>[0];

interface CreateAddendumArg {
  description: string;
  reason: string;
  studyUuid: string;
}

interface CreateAddendumResp {
  uuid: string;
  userFirstName?: string;
  reason?: string;
  createdAt?: string;
  description?: string;
}

type MutationTrigger = (arg: CreateAddendumArg) => {
  unwrap: () => Promise<CreateAddendumResp>;
};

interface NormalizedAddendum {
  author: string;
  reason: string;
  createdAt: string;
  text: string;
}

interface UseAddendumFlowArgs {
  currentReport: MReport | null;
  studyUuid: string;
  activeUserEmail: string;
  signerName: string;
  createaddendum: MutationTrigger;
  notification: NotificationApi;
  showAddendumModal: boolean;
}

export interface AddendumFlowState {
  addendumVisible: boolean;
  previousAddendums: NormalizedAddendum[];
  hasOpenAddendum: boolean;
  addendumOpenedByEmail: string | undefined;
  addendumOpenedByName: string;
  handleAddendumOk: (payload: {
    reason: string;
    text: string;
  }) => Promise<void>;
  handleAddAddendum: () => void;
  setAddendumVisible: (open: boolean) => void;
}

export function useAddendumFlow({
  currentReport,
  studyUuid,
  signerName,
  createaddendum,
  notification: api,
  showAddendumModal,
}: UseAddendumFlowArgs): AddendumFlowState {
  const [addendumVisible, setAddendumVisible] = useState(false);
  const [previousAddendums, setPreviousAddendums] = useState<
    NormalizedAddendum[]
  >([]);

  const hasOpenAddendum =
    !!currentReport?.addendum && !currentReport.isFinalized;
  const addendumOpenedByEmail = currentReport?.addendum?.userEmail;
  const addendumOpenedByName =
    `${currentReport?.addendum?.userFirstName || ''} ${currentReport?.addendum?.userLastName || ''}`.trim();

  // Hydrate previousAddendums from the report payload. Tolerates either an
  // `addendums` array or a single `addendum`, and normalizes field names so
  // the list view renders consistently.
  useEffect(() => {
    if (!currentReport) return;
    const cr = currentReport as any;
    const existingArr = cr.addendums || (cr.addendum ? [cr.addendum] : []);
    if (!Array.isArray(existingArr)) return;

    const normalized: NormalizedAddendum[] = existingArr.map((a: any) => ({
      author:
        a.userFirstName || a.userLastName
          ? `${a.userFirstName || ''} ${a.userLastName || ''}`.trim()
          : a.userEmail || a.author || a.signer || 'Unknown',
      reason: a.reason,
      createdAt: a.createdAt || a.updatedAt,
      text: a.description || a.text || '',
    }));
    setPreviousAddendums(normalized);
  }, [currentReport]);

  const handleAddendumOk = useCallback(
    async (payload: { reason: string; text: string }) => {
      try {
        const resp = await createaddendum({
          description: payload.text,
          reason: payload.reason,
          studyUuid,
        }).unwrap();

        const newAddendum: NormalizedAddendum = {
          author: resp.userFirstName || signerName,
          reason: resp.reason || payload.reason,
          createdAt: resp.createdAt || new Date().toISOString(),
          text: resp.description || payload.text,
        };
        setPreviousAddendums((p) => [newAddendum, ...p]);

        api.success({
          message: 'Addendum created',
          description: 'Report addendum created successfully',
        });
      } catch (e) {
        api.error({
          message: 'Error creating addendum',
          description: (e as any)?.data?.detail || String(e),
        });
      } finally {
        setAddendumVisible(false);
      }
    },
    [createaddendum, studyUuid, api, signerName]
  );

  const handleAddAddendum = useCallback(() => {
    if (showAddendumModal) {
      setAddendumVisible(true);
      return;
    }
    handleAddendumOk({ reason: 'CONCURRENCE', text: '' }).catch(
      () => undefined
    );
  }, [handleAddendumOk, showAddendumModal]);

  return {
    addendumVisible,
    previousAddendums,
    hasOpenAddendum,
    addendumOpenedByEmail,
    addendumOpenedByName,
    handleAddendumOk,
    handleAddAddendum,
    setAddendumVisible,
  };
}
